// Persistent shell rendered by AdminShell. The sidebar mounts once per
// station entry and stays mounted across tab clicks (the previous
// architecture re-mounted on every navigation because each page wrapped
// the layout itself).
//
// Active highlight is derived from the URL via NavLink's isActive; pages
// no longer need to pass an `active` prop.
//
// CTV-759 / ADR-0036: takes the AdminContext rather than a PublisherConfig,
// so it renders in both shell modes. The sidebar keeps a CHROME STATION
// either way — the current station in single mode, the Scope collapse target
// in aggregated mode — which is what lets every slug-dependent affordance
// (materialsEnabled, channel-settings, the NavLink hrefs) keep working with
// no mode branch. Only the three scope-flexible hrefs differ by mode.
//
// The station-bound hrefs encode the Scope collapse directly, so clicking
// one needs no redirect; AdminShell's typed-URL guard is the only other
// place the rule exists.

import React from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { AlertTriangle, Clapperboard, CreditCard, FileText, Handshake, HelpCircle, Inbox, KeyRound, LayoutDashboard, Mail, Megaphone, SlidersHorizontal, Tag, LogOut, Settings, Sparkles, Upload } from 'lucide-react';
import { useAuth } from '../auth/useAuth';
import { materialsForRole } from '../materials/materialsCatalog';
import { rankOf } from '../shared/roleRank';
import { displayedName, usePublisherConfig } from '../../../data/publisherConfigs';
import MarketplaceLogo from '../MarketplaceLogo';
import { useAdminHelpUnread } from '../help/useAdminHelpUnread';
import { useAdminPublicaUploadsCount } from './useAdminPublicaUploadsCount';
import { usePayoutSetupStatus } from './usePayoutSetupStatus';
import { scopeForContext } from './stationScopeRequest';
import PayoutSetupBanner from './PayoutSetupBanner';
import { ALL_STATIONS_SLUG } from './lastAdminStation';
import type { AdminContext } from './useAdminContext';
import { publisherBillingEnabled } from './payoutsFeature';

interface PublisherAdminLayoutProps {
  context: AdminContext;
  children: React.ReactNode;
}

const navLinkClass = (isActive: boolean) =>
  `flex items-center gap-2 px-3 py-2 text-sm rounded-md transition-colors ${
    isActive ? 'bg-slate-900 text-white' : 'text-slate-300 hover:bg-slate-800 hover:text-white'
  }`;

const PublisherAdminLayout: React.FC<PublisherAdminLayoutProps> = ({ context, children }) => {
  const { user, signOut } = useAuth();
  const isSuperAdmin = user?.rawRole === 'super_admin';
  // Manager+ gates Referrals (CTV-671: commission %s and per-rep revenue),
  // Settings (CTV-787: who can reach the station, and where its payouts go),
  // and Billing (the payout ledger). The API enforces all three
  // independently; hiding a NavLink is a courtesy, not a control. Promotions
  // stays visible to reps but read-only.
  const isManagerPlus = rankOf(user?.rawRole) >= rankOf('manager');
  const { count: adminHelpUnread } = useAdminHelpUnread(isSuperAdmin);
  const { count: publicaUploadsCount } = useAdminPublicaUploadsCount(isSuperAdmin);

  const aggregated = context.mode === 'aggregated';
  // The chrome station. In aggregated mode its config comes from the same
  // cached usePublisherConfig the single-station gate already warmed, so it
  // is normally a cache hit.
  const chromeSlug = aggregated ? context.collapseSlug : context.slug;
  const { config: collapseConfig } = usePublisherConfig(
    aggregated ? context.collapseSlug : undefined,
  );
  const chromeConfig = context.mode === 'single' ? context.config : collapseConfig;
  // Scope-flexible tabs point at the sentinel in aggregated mode; everything
  // else points at the collapse target, which IS the collapse.
  const scopeSlug = aggregated ? ALL_STATIONS_SLUG : context.slug;

  // CTV-743: only manager+ can act on this (the whole /stripe/* surface is
  // manager-gated), so only manager+ is nagged about it. CTV-759: one scoped
  // request covers every station in scope, so the aggregated roll-up costs
  // the same as the single-station check.
  // CTV-776 (see payoutsFeature.ts): the whole publisher money surface, the
  // Billing tab included, is hidden until Stripe Connect is live, so skip the
  // readiness lookup too.
  const payoutsVisible = publisherBillingEnabled();
  const { needsSetup: payoutsNeedSetup, unready: payoutsUnready } = usePayoutSetupStatus(
    scopeForContext(context),
    isManagerPlus && payoutsVisible,
  );
  const { pathname } = useLocation();

  return (
    <div className="min-h-screen bg-slate-50 flex">
      {/* Sidebar is `sticky top-0 h-screen` so the Sign out block at the
          bottom stays pinned to the viewport regardless of main-content
          scroll. Overflow lives on the nav so a long menu can scroll
          independently of the header + sign-out footer. */}
      <aside className="w-60 bg-slate-950 text-white flex flex-col sticky top-0 h-screen">
        <div className="px-4 py-5 border-b border-slate-800">
          <MarketplaceLogo variant="light" className="mb-3" />
          <div className="text-xs uppercase tracking-wider text-slate-400">Publisher Admin</div>
          {/* CTV-759: static text, not a picker. StationSwitcher is deleted —
              it was a NAVIGATION control among navigation links, which is what
              made an all-to-single transition feel like it needed a modal. The
              Station scope selector lives next to each page heading now, where
              it reads as "here is what this page is showing". Classes are the
              switcher's own solo-fallback so header height doesn't shift
              between modes. */}
          <div className="text-base font-semibold mt-1 truncate">
            {aggregated
              ? 'All stations'
              : chromeConfig
                ? displayedName(chromeConfig)
                : ''}
          </div>
        </div>
        <nav className="flex-1 overflow-y-auto p-3 space-y-1">
          {/* CTV-808: every role has an Overview. A rep's is the Metrics
              section alone, narrowed server-side to their referrals, and it
              is station-bound: the href points at the chrome station, never
              the All-stations sentinel. (CTV-673 had removed the rep
              Overview on 2026-08-12 because every section was manager+.) */}
          <NavLink to={`/admin/${isManagerPlus ? scopeSlug : chromeSlug}`} end className={({ isActive }) => navLinkClass(isActive)}>
            <LayoutDashboard className="w-4 h-4" />
            Overview
          </NavLink>
          {/* CTV-673 audit: submission review is manager+ work; the API
              refuses sales_rep on every submissions route regardless. */}
          {isManagerPlus && (
          <NavLink to={`/admin/${scopeSlug}/submissions`} className={({ isActive }) => navLinkClass(isActive)}>
            <Inbox className="w-4 h-4" />
            Submissions
          </NavLink>
          )}
          {/* CTV-673 audit made the roster manager+. CTV-808: a rep gets it
              too, narrowed to the businesses that used their referral code,
              station-bound like their Overview. */}
          <NavLink to={`/admin/${isManagerPlus ? scopeSlug : chromeSlug}/advertisers`} className={({ isActive }) => navLinkClass(isActive)}>
            <Megaphone className="w-4 h-4" />
            Advertisers
          </NavLink>
          {isManagerPlus ? (
            <NavLink to={`/admin/${chromeSlug}/referrals`} className={({ isActive }) => navLinkClass(isActive)}>
              <Handshake className="w-4 h-4" />
              Referrals
            </NavLink>
          ) : (
            // CTV-673: reps get their own scoped stats view instead of the
            // manager roster (the API only ever returns their own record).
            <NavLink to={`/admin/${chromeSlug}/my-referrals`} className={({ isActive }) => navLinkClass(isActive)}>
              <Handshake className="w-4 h-4" />
              My Referrals
            </NavLink>
          )}
          <NavLink to={`/admin/${chromeSlug}/promotions`} className={({ isActive }) => navLinkClass(isActive)}>
            <Tag className="w-4 h-4" />
            Promotions
          </NavLink>
          {/* CTV-752: the tab shows only when the caller would find something
              inside it — the station's switch is on AND at least one sheet
              passes their role/draft gate (an all-draft catalogue hides it).
              Super_admins always keep the tab (they see drafts), with a
              Hidden pill when the station switch is off. */}
          {(isSuperAdmin
            || (chromeConfig?.materialsEnabled !== false && materialsForRole(user?.rawRole).length > 0)) && (
            <NavLink to={`/admin/${chromeSlug}/materials`} className={({ isActive }) => navLinkClass(isActive)}>
              <FileText className="w-4 h-4" />
              Materials
              {chromeConfig?.materialsEnabled === false && (
                <span className="ml-auto rounded-full bg-amber-500/20 px-2 py-0.5 text-[10px] font-semibold text-amber-300">
                  Hidden
                </span>
              )}
            </NavLink>
          )}
          {/* CTV-776: Billing replaces "Payments (Stripe)". CTV-787 left it
              the payout ledger by delivery month alone: the payout METHOD is
              configuration, so it moved to Station settings. Scope-flexible
              (the ledger groups by station), so it points at the sentinel in
              aggregated mode. */}
          {isManagerPlus && payoutsVisible && (
          <NavLink to={`/admin/${scopeSlug}/billing`} className={({ isActive }) => navLinkClass(isActive)}>
            <CreditCard className="w-4 h-4" />
            Billing
          </NavLink>
          )}
          {/* CTV-787: Station settings, last in the main group and just above
              the Super admin section, because configuration is where you go
              least often. Station-bound, so it points at the chrome station
              even in aggregated mode. */}
          {isManagerPlus && (
          <NavLink to={`/admin/${chromeSlug}/settings`} className={({ isActive }) => navLinkClass(isActive)}>
            {/* CTV-743: a dot rather than a count, because there is nothing to
                count. Same badge shape as the Help Center unread marker so the
                sidebar has one vocabulary for "needs your attention". It rode
                on Billing until CTV-787 moved the payout setup under here. */}
            <span className="relative">
              <Settings className="w-4 h-4" />
              {payoutsVisible && payoutsNeedSetup && (
                <span
                  data-testid="payouts-nav-alert"
                  aria-label="Payouts not set up"
                  className="absolute -top-1 -right-1 w-[10px] h-[10px] rounded-full bg-red-500 ring-2 ring-slate-950"
                />
              )}
            </span>
            Settings
          </NavLink>
          )}
          {/* Super_admin-only — channel CRUD lives inside the per-station admin
              shell rather than as standalone /admin/publishers* pages.
              "Onboard channel" keeps its global /admin/onboard URL; the
              onboard page re-renders THIS sidebar via the remembered station
              (see rememberAdminStation), purely as navigation chrome. */}
          {isSuperAdmin && (
            <>
              <div className="pt-3 pb-1 px-3 text-[10px] uppercase tracking-wider text-slate-500">
                Super admin
              </div>
              <NavLink
                to={`/admin/${chromeSlug}/channel-settings`}
                className={({ isActive }) => navLinkClass(isActive)}
              >
                {/* CTV-787: SlidersHorizontal, not the gear. The gear is
                    Station settings now, and two gears in one sidebar would
                    read as the same destination twice. */}
                <SlidersHorizontal className="w-4 h-4" />
                Channel settings
              </NavLink>
              <NavLink to="/admin/onboard" className={({ isActive }) => navLinkClass(isActive)}>
                <Sparkles className="w-4 h-4" />
                Onboard channel
              </NavLink>
              {/* CTV-732: open Publica Upload Tasks. The badge is the standing
                  count; the blocking subset also fires an immediate ops email. */}
              <NavLink to="/admin/publica-uploads" className={({ isActive }) => navLinkClass(isActive)}>
                <span className="relative">
                  <Upload className="w-4 h-4" />
                  {publicaUploadsCount > 0 && (
                    <span className="absolute -top-1 -right-1 min-w-[14px] h-[14px] px-0.5 rounded-full bg-red-500 text-[9px] font-bold text-white flex items-center justify-center">
                      {publicaUploadsCount > 9 ? '9+' : publicaUploadsCount}
                    </span>
                  )}
                </span>
                Publica uploads
              </NavLink>
              <NavLink to="/admin/publica-push-jobs" className={({ isActive }) => navLinkClass(isActive)}>
                <AlertTriangle className="w-4 h-4" />
                Publica push jobs
              </NavLink>
              {/* CTV-800: staff-only Waymark commercials for direct clients.
                  Sits next to Waymark credentials because the two are the
                  same vendor, and, like it, the Studio is its own full page
                  rather than a station tab. */}
              <NavLink to="/admin/studio" className={({ isActive }) => navLinkClass(isActive)}>
                <Clapperboard className="w-4 h-4" />
                Studio
              </NavLink>
              <NavLink to="/admin/waymark-credentials" className={({ isActive }) => navLinkClass(isActive)}>
                <KeyRound className="w-4 h-4" />
                Waymark credentials
              </NavLink>
              {/* CTV-784: templated publisher emails, filled in and copied (never sent). */}
              <NavLink to="/admin/email-templates" className={({ isActive }) => navLinkClass(isActive)}>
                <Mail className="w-4 h-4" />
                Email templates
              </NavLink>
              <NavLink to="/admin/help-center" className={({ isActive }) => navLinkClass(isActive)}>
                <span className="relative">
                  <HelpCircle className="w-4 h-4" />
                  {adminHelpUnread > 0 && (
                    <span className="absolute -top-1 -right-1 min-w-[14px] h-[14px] px-0.5 rounded-full bg-red-500 text-[9px] font-bold text-white flex items-center justify-center">
                      {adminHelpUnread > 9 ? '9+' : adminHelpUnread}
                    </span>
                  )}
                </span>
                Help Center
              </NavLink>
            </>
          )}
        </nav>
        <div className="p-3 border-t border-slate-800">
          <div className="text-xs text-slate-400 mb-2 truncate">{user?.email}</div>
          <button
            type="button"
            onClick={signOut}
            className="w-full flex items-center gap-2 px-3 py-2 text-sm text-slate-300 hover:bg-slate-800 hover:text-white rounded-md"
          >
            <LogOut className="w-4 h-4" />
            Sign out
          </button>
        </div>
      </aside>
      <main className="flex-1 min-w-0 p-6 md:p-10">
        {/* Mounted here so it survives tab clicks without refetching, but it
            renders on the OVERVIEW only (the component owns that rule). The
            sidebar dot carries the unresolved state on every other tab. */}
        {payoutsVisible && (
          <PayoutSetupBanner
            slug={scopeSlug}
            needsSetup={payoutsNeedSetup}
            pathname={pathname}
            unreadyStations={payoutsUnready}
          />
        )}
        {children}
      </main>
    </div>
  );
};

export default PublisherAdminLayout;
